import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  IconButton,
  Modal,
  TextField,
  Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import type { EditOrderLineModalProps } from "../interfaces/modals";
import {
  useGetProductDetailForOrder,
  useGetProductItemDetailForOrder,
  type ProductDetailRow,
  type ProductItem
} from "../../../hooks/api/useOrderInventory";
import { useUpdateOrderItem } from "../../../hooks/api/useSalesOrder";
import { isOrderMutationRejectedError } from "../../../hooks/api/orderMutationErrors";
import { orderItemLineLabel } from "../utils/orderUtils";
import { SellingMode } from "../enums/orderStatus";
import { normalizeSellingMode } from "../utils/scanResponseUtils";
import { messageForOrderMutationCode } from "../utils/orderErrorMessages";

function resolveSellingMode(product: ProductDetailRow | null): string {
  if (!product?.sellingMode) return SellingMode.SINGLE_ITEM;
  return normalizeSellingMode(product.sellingMode) || SellingMode.SINGLE_ITEM;
}

/** Approximate max quantity this line can be set to (pool + current line reservation). */
function maxAllocatable(
  remainingQuantity: number,
  reservedQuantity: number | null | undefined,
  currentOrderQuantity: number
): number {
  const rem = Number(remainingQuantity) || 0;
  const res = Number(reservedQuantity ?? 0) || 0;
  const cur = Number(currentOrderQuantity) || 0;
  return Math.max(1, rem - res + cur);
}

const EditOrderLineModal: React.FC<EditOrderLineModalProps> = ({
  open,
  onClose,
  orderId,
  line,
  onUpdated
}) => {
  const { t } = useKivunovaTranslation();
  const productItemDetailMutation = useGetProductItemDetailForOrder();
  const productDetailMutation = useGetProductDetailForOrder();
  const { mutateAsync: fetchProductItemDetail } = productItemDetailMutation;
  const { mutateAsync: fetchProductDetail } = productDetailMutation;
  const updateOrderItem = useUpdateOrderItem({ suppressErrorBanner: true });

  const [productItem, setProductItem] = useState<ProductItem | null>(null);
  const [product, setProduct] = useState<ProductDetailRow | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [quantityStr, setQuantityStr] = useState("1");

  const sellingMode = useMemo(() => resolveSellingMode(product), [product]);

  const currentQty = line?.quantity ?? 0;

  const maxPool = useMemo(() => {
    if (!productItem) return 1;
    return maxAllocatable(
      productItem.remainingQuantity,
      productItem.reservedQuantity,
      currentQty
    );
  }, [productItem, currentQty]);

  const maxUnitInt = Math.max(1, Math.floor(maxPool));

  const reset = useCallback(() => {
    setProductItem(null);
    setProduct(null);
    setLoadError(null);
    setSubmitError(null);
    setQuantityStr("1");
  }, []);

  useEffect(() => {
    if (!open || !line) {
      reset();
      return;
    }

    let cancelled = false;
    setLoadError(null);
    setSubmitError(null);
    setProductItem(null);
    setProduct(null);
    setQuantityStr(String(line.quantity));

    (async () => {
      try {
        // Hook returns ProductItem | null directly — no wrapper shape
        const itemResult = await fetchProductItemDetail({ id: line.itemId });
        if (cancelled) return;

        if (!itemResult) {
          setLoadError(
            t(
              "edit-line-load-item-failed",
              "Could not load inventory details for this line. Try again."
            )
          );
          return;
        }
        setProductItem(itemResult);

        // Hook now returns ProductDetailRow | null directly — no wrapper shape
        const prodResult = await fetchProductDetail({
          id: itemResult.productId
        });
        if (cancelled) return;

        if (!prodResult) {
          setLoadError(
            t(
              "edit-line-load-product-failed",
              "Could not load product details for this line. Try again."
            )
          );
          return;
        }
        setProduct(prodResult);
      } catch {
        if (!cancelled) {
          setLoadError(
            t(
              "edit-line-load-failed",
              "Could not load line details. Check your connection and try again."
            )
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, line?.itemId, line?.quantity, reset]);
  // Re-fetch only when modal opens or line id/qty changes. Omit mutation result
  // objects and `t` — they change when isPending updates and caused infinite requests.

  const submitBusy = updateOrderItem.isPending;
  const loadBusy =
    productItemDetailMutation.isPending || productDetailMutation.isPending;

  const handleSubmit = async () => {
    if (!line || !productItem) return;
    setSubmitError(null);

    const mode = sellingMode;

    if (mode === SellingMode.SINGLE_ITEM) {
      try {
        const order = await updateOrderItem.mutateAsync({
          orderId,
          productItemId: line.itemId,
          quantity: 1
        });
        if (order) {
          await onUpdated();
          onClose();
        } else {
          setSubmitError(
            t(
              "edit-line-update-failed",
              "Could not update this line. Please try again."
            )
          );
        }
      } catch (e) {
        if (isOrderMutationRejectedError(e)) {
          setSubmitError(messageForOrderMutationCode(e.apiCode, t));
        } else {
          setSubmitError(
            t(
              "edit-line-unexpected-error",
              "Something went wrong. Please try again."
            )
          );
        }
      }
      return;
    }

    if (mode === SellingMode.MEASURED) {
      const q = parseFloat(quantityStr.replace(",", "."));
      if (Number.isNaN(q) || q <= 0) {
        setSubmitError(
          t("edit-line-invalid-qty", "Enter a quantity greater than zero.")
        );
        return;
      }
      if (q > maxPool + 1e-9) {
        setSubmitError(
          t(
            "edit-line-qty-exceeds",
            "Quantity cannot exceed the available amount."
          )
        );
        return;
      }
      try {
        const order = await updateOrderItem.mutateAsync({
          orderId,
          productItemId: line.itemId,
          quantity: q
        });
        if (order) {
          await onUpdated();
          onClose();
        } else {
          setSubmitError(
            t(
              "edit-line-update-failed",
              "Could not update this line. Please try again."
            )
          );
        }
      } catch (e) {
        if (isOrderMutationRejectedError(e)) {
          setSubmitError(messageForOrderMutationCode(e.apiCode, t));
        } else {
          setSubmitError(
            t(
              "edit-line-unexpected-error",
              "Something went wrong. Please try again."
            )
          );
        }
      }
      return;
    }

    const qty = parseInt(quantityStr, 10);
    if (!Number.isFinite(qty) || qty < 1) {
      setSubmitError(
        t("edit-line-invalid-qty-whole", "Enter a valid whole number quantity.")
      );
      return;
    }
    if (qty > maxUnitInt) {
      setSubmitError(
        t(
          "edit-line-qty-exceeds",
          "Quantity cannot exceed the available amount."
        )
      );
      return;
    }

    try {
      const order = await updateOrderItem.mutateAsync({
        orderId,
        productItemId: line.itemId,
        quantity: qty
      });
      if (order) {
        await onUpdated();
        onClose();
      } else {
        setSubmitError(
          t(
            "edit-line-update-failed",
            "Could not update this line. Please try again."
          )
        );
      }
    } catch (e) {
      if (isOrderMutationRejectedError(e)) {
        setSubmitError(messageForOrderMutationCode(e.apiCode, t));
      } else {
        setSubmitError(
          t(
            "edit-line-unexpected-error",
            "Something went wrong. Please try again."
          )
        );
      }
    }
  };

  const productLabel = line
    ? orderItemLineLabel(line, product?.name ?? null)
    : "";

  const quantityField =
    !product || !productItem || loadError ? (
      <TextField
        label={t("quantity", "Quantity")}
        value=""
        fullWidth
        size="small"
        disabled
        placeholder={t("edit-line-loading-qty", "Loading…")}
      />
    ) : sellingMode === SellingMode.SINGLE_ITEM ? (
      <TextField
        label={t("quantity", "Quantity")}
        value="1"
        fullWidth
        disabled
        size="small"
        helperText={t(
          "add-item-single-item-hint",
          "This product is sold as a single item (quantity is always 1)."
        )}
      />
    ) : sellingMode === SellingMode.UNIT_BASED ? (
      <TextField
        label={t("quantity", "Quantity")}
        type="number"
        value={quantityStr}
        onChange={(e) => setQuantityStr(e.target.value)}
        fullWidth
        size="small"
        disabled={submitBusy}
        inputProps={{
          min: 1,
          max: maxUnitInt,
          step: 1
        }}
        helperText={`${t("add-item-unit-hint", "Whole units only.")} ${t("max", "Max")}: ${maxUnitInt}.`}
      />
    ) : (
      <TextField
        label={t("quantity", "Quantity")}
        type="number"
        value={quantityStr}
        onChange={(e) => setQuantityStr(e.target.value)}
        fullWidth
        size="small"
        disabled={submitBusy}
        inputProps={{
          min: 0,
          max: maxPool,
          step: 0.01
        }}
        helperText={`${t(
          "add-item-measured-hint",
          "Measured sale (e.g. weight or length). Fractional quantities may be rounded when saving."
        )} ${t("max", "Max")}: ${maxPool}.`}
      />
    );

  return (
    <Modal open={open} onClose={submitBusy ? undefined : onClose}>
      <Box className="modal-styles">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {t("edit-order-line", "Edit order line")}
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={submitBusy}>
            <CloseIcon />
          </IconButton>
        </Box>

        {line && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {productLabel}
          </Typography>
        )}

        {loadError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setLoadError(null)}
          >
            {loadError}
          </Alert>
        )}

        {submitError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setSubmitError(null)}
          >
            {submitError}
          </Alert>
        )}

        {loadBusy && !loadError && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("edit-line-loading", "Loading product details…")}
          </Typography>
        )}

        {quantityField}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 3,
            flexWrap: "wrap"
          }}
        >
          <LoadingButton
            variant="outlined"
            onClick={onClose}
            disabled={submitBusy}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              backgroundColor: "white",
              color: BRAND_COLOR,
              borderColor: BRAND_COLOR,
              "&:hover": {
                borderColor: BRAND_HOVER,
                backgroundColor: BRAND_HOVER,
                color: "white"
              }
            }}
          >
            {t("cancel", "Cancel")}
          </LoadingButton>
          <LoadingButton
            variant="contained"
            disableElevation
            onClick={() => void handleSubmit()}
            loading={submitBusy}
            disabled={!!loadError || loadBusy || !productItem || !product}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              backgroundColor: BRAND_COLOR,
              "&:hover": { backgroundColor: BRAND_HOVER }
            }}
          >
            {t("save", "Save")}
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditOrderLineModal;
